/**
 * The handbook's controls, in the order somebody writing a procedure reaches
 * for them: the sentence, then the structure, then the named blocks.
 *
 * Every button here maps to one thing the reader styles and the server allows.
 * That is the whole point of a handbook editor over a general one - there is no
 * font size, no colour, no alignment, because a guide that carries those is a
 * guide that stops looking like the twenty-one beside it.
 */
import type { Editor } from "@tiptap/react";
import {
  Bold,
  Code2,
  Italic,
  Link as LinkIcon,
  List,
  ListChecks,
  ListOrdered,
  Image as ImageIcon,
  Mail,
  Redo,
  SquareCheckBig,
  StickyNote,
  Table as TableIcon,
  TriangleAlert,
  Underline as UnderlineIcon,
  Undo,
} from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";

/** One control. `on` paints it as active, `off` greys it out. */
function Tb({
  title,
  on,
  off,
  onClick,
  children,
}: {
  title: string;
  on?: boolean;
  off?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn("crm-kb-tb", on && "is-on")}
      title={title}
      aria-label={title}
      aria-pressed={on}
      disabled={off}
      // Mouse down rather than click: a click first moves focus out of the
      // editor, and a command that runs with no selection does nothing.
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      {children}
    </button>
  );
}

export function KbToolbar({ editor }: { editor: Editor }) {
  const t = useT();

  /** Ask for a link target, keeping whatever is already on the selection. */
  function link() {
    const current = String(editor.getAttributes("link").href ?? "");
    const next = window.prompt(t("kb.tb.linkPrompt"), current || "/guides/");
    if (next === null) return;
    const chain = editor.chain().focus().extendMarkRange("link");
    if (!next.trim()) chain.unsetLink().run();
    else chain.setLink({ href: next.trim() }).run();
  }

  /** A fresh picture slot, keyed from its position so two are never the same. */
  function shot() {
    const used = new Set<string>();
    editor.state.doc.descendants((n) => {
      if (n.type.name === "kbShot") used.add(String(n.attrs.key ?? ""));
    });
    let n = used.size + 1;
    while (used.has(`pilt-${n}`)) n += 1;
    editor.chain().focus().insertContent({ type: "kbShot", attrs: { key: `pilt-${n}`, caption: "" } }).run();
  }

  return (
    <div className="crm-kb-tbar">
      <div className="crm-kb-tgroup">
        <Tb title={t("kb.tb.bold")} on={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="size-4" />
        </Tb>
        <Tb title={t("kb.tb.italic")} on={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.underline")}
          on={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="size-4" />
        </Tb>
        <Tb title={t("kb.tb.code")} on={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}>
          <Code2 className="size-4" />
        </Tb>
        <Tb title={t("kb.tb.link")} on={editor.isActive("link")} onClick={link}>
          <LinkIcon className="size-4" />
        </Tb>
      </div>

      <div className="crm-kb-tgroup">
        {([2, 3, 4] as const).map((level) => (
          <Tb
            key={level}
            title={t(`kb.tb.h${level}`)}
            on={editor.isActive("heading", { level })}
            onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          >
            <span className="crm-kb-tb-t">H{level}</span>
          </Tb>
        ))}
        <Tb
          title={t("kb.tb.lede")}
          on={editor.isActive("paragraph", { lede: true })}
          onClick={() =>
            editor
              .chain()
              .focus()
              .setNode("paragraph", { lede: !editor.isActive("paragraph", { lede: true }) })
              .run()
          }
        >
          <span className="crm-kb-tb-t">{t("kb.tb.ledeShort")}</span>
        </Tb>
      </div>

      <div className="crm-kb-tgroup">
        <Tb
          title={t("kb.tb.bullets")}
          on={editor.isActive("bulletList", { check: false })}
          onClick={() => editor.chain().focus().toggleBulletList().updateAttributes("bulletList", { check: false }).run()}
        >
          <List className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.check")}
          on={editor.isActive("bulletList", { check: true })}
          onClick={() => {
            const chain = editor.chain().focus();
            if (!editor.isActive("bulletList")) chain.toggleBulletList();
            chain.updateAttributes("bulletList", { check: true }).run();
          }}
        >
          <ListChecks className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.numbers")}
          on={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="size-4" />
        </Tb>
      </div>

      <div className="crm-kb-tgroup">
        <Tb
          title={t("kb.tb.note")}
          on={editor.isActive("kbCallout", { tone: "note" })}
          onClick={() => editor.chain().focus().wrapIn("kbCallout", { tone: "note" }).run()}
        >
          <StickyNote className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.warn")}
          on={editor.isActive("kbCallout", { tone: "warn" })}
          onClick={() => editor.chain().focus().wrapIn("kbCallout", { tone: "warn" }).run()}
        >
          <TriangleAlert className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.do")}
          on={editor.isActive("kbDo")}
          onClick={() => editor.chain().focus().wrapIn("kbDo").run()}
        >
          <SquareCheckBig className="size-4" />
        </Tb>
        <Tb
          title={t("kb.tb.mail")}
          on={editor.isActive("kbMail")}
          onClick={() => editor.chain().focus().wrapIn("kbMail").run()}
        >
          <Mail className="size-4" />
        </Tb>
      </div>

      <div className="crm-kb-tgroup">
        <Tb
          title={t("kb.tb.table")}
          onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
        >
          <TableIcon className="size-4" />
        </Tb>
        <Tb title={t("kb.tb.shot")} onClick={shot}>
          <ImageIcon className="size-4" />
        </Tb>
      </div>

      <div className="crm-kb-tgroup crm-kb-tgroup-end">
        <Tb title={t("kb.tb.undo")} off={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <Undo className="size-4" />
        </Tb>
        <Tb title={t("kb.tb.redo")} off={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <Redo className="size-4" />
        </Tb>
      </div>
    </div>
  );
}
